enum Tenants {
  Mobiworkx = 'mobiworkx',
  Lifepoints = 'lifepoints',
  Qmee = 'qmee',
}

/**
 * Returns the current tenant based on the `NEXT_PUBLIC_TENANT` environment variable.
 *
 * Accepts three optional argument forms:
 * - **Tenant enum value** – returns a `boolean` indicating whether the passed tenant
 *   matches the current tenant.
 * - **Full map** (`Record<Tenants, T>`) – returns the value mapped to the current
 *   tenant; the return type is inferred and non-nullable.
 * - **Partial map** (`Partial<Record<Tenants, T>>`) – same as above, but the return
 *   type includes `undefined` since the current tenant may not have a mapped value.
 *
 * @example
 * // Returns the current Tenants enum value
 * const tenant = useTenant();
 *
 * @example
 * // Returns the value mapped to the current tenant (all keys present → non-nullable)
 * const label = useTenant({ mobiworkx: 'Mobiworkx', lifepoints: 'LifePoints', qmee: 'Qmee' });
 *
 * @example
 * // Partial map – return type includes undefined
 * const icon = useTenant({ qmee: QmeeIcon, mobiworkx: MxIcon });
 *
 * @example
 * // Pass a tenant to check if it matches the current tenant
 * const isQmee = useTenant(Tenants.Qmee); // boolean
 */
export function useTenant(): Tenants;
export function useTenant(tenant: Tenants): boolean;
export function useTenant<T extends Record<Tenants, unknown>>(map: T): T[Tenants];
export function useTenant<T extends Partial<Record<Tenants, unknown>>>(
  map: T,
): T[keyof T & Tenants] | undefined;
export function useTenant<T extends Partial<Record<Tenants, unknown>>>(
  map?: T | Tenants,
): Tenants | boolean | T[keyof T & Tenants] | undefined {
  const tenant = (process.env.NEXT_PUBLIC_TENANT || 'mobiworkx') as Tenants;
  if (map === undefined) return tenant;
  if (typeof map === 'string') return map === tenant;
  return map[tenant] as T[keyof T & Tenants] | undefined;
}
